import { Link } from "@tanstack/react-router";

import { DateBlock } from "@/components/site/DateBlock";
import { HERO_IMAGE_HEIGHT } from "@/components/site/hero-size";
import type { Meeting } from "@/lib/content";
import { meetingDateParts, meetingLocationLabel } from "@/lib/meeting-format";
import featuredFallback from "@/assets/featured-1280.webp.asset.json";
import { CONTAINED_HERO_SIZES } from "@/lib/cover-image";

const FALLBACK_IMAGE = featuredFallback.url;

export function HeroFeature({
  meeting,
  showBadge = true,
  showReadMore = true,
  full = true,
  priority = false,
  headingLevel = 2,
  contained = false,
}: {
  meeting: Meeting;
  showBadge?: boolean;
  showReadMore?: boolean;
  full?: boolean;
  priority?: boolean;
  headingLevel?: 1 | 2 | 3;
  contained?: boolean;
}) {
  const Heading = `h${headingLevel}` as "h1" | "h2" | "h3";
  const image = meeting.cover_image_url ?? FALLBACK_IMAGE;
  const parts = meetingDateParts(meeting);
  const location = meetingLocationLabel(meeting);
  const isUpcoming = meeting.is_upcoming || meeting.category === "Upcoming";
  const tone = isUpcoming ? "accent" : "dark";

  return (
    <section
      className={full ? "relative w-full bg-surface" : "relative w-full overflow-hidden bg-surface"}
      aria-label="Featured meeting"
    >
      <img
        src={image}
        srcSet={meeting.cover_image_srcset}
        sizes={contained ? CONTAINED_HERO_SIZES : "100vw"}
        alt={meeting.title}
        width={1400}
        height={933}
        className={`${HERO_IMAGE_HEIGHT} w-full object-cover`}
        loading={priority ? "eager" : "lazy"}
        fetchPriority={priority ? "high" : "auto"}
        decoding="async"
      />

      <div className="absolute inset-0">
        {parts && (
          <div className="absolute right-5 top-5 sm:hidden">
            <DateBlock parts={parts} tone={isUpcoming ? "accent" : "light"} />
          </div>
        )}
        <div className="mx-auto flex h-full w-full max-w-[1400px] flex-col justify-end px-5 pb-8 lg:px-8 lg:pb-14">
          {showBadge && (
            <span className="fc-eyebrow inline-block self-start bg-accent px-4 py-2 text-primary-foreground">
              {meeting.category}
            </span>
          )}

          <div className="relative mt-2 max-w-[640px] bg-card px-6 py-5 lg:px-8 lg:py-7">
            {parts && (
              <div className="absolute bottom-full right-0 mb-2 hidden sm:block">
                <DateBlock parts={parts} tone={tone} />
              </div>
            )}
            {location && <p className="fc-meta text-accent">{location}</p>}
            <Heading className={`fc-h2 ${location ? "mt-1" : ""} text-foreground`}>{meeting.title}</Heading>
          </div>

          {showReadMore && (
            <Link
              to="/meetings/$slug"
              params={{ slug: meeting.slug }}
              aria-label={`Read more: ${meeting.title}`}
              className="mt-2 inline-block self-start bg-primary px-6 py-3 fc-body text-primary-foreground transition-opacity hover:opacity-85"
            >
              Read More
              <span className="sr-only">: {meeting.title}</span>
            </Link>
          )}
        </div>
      </div>
    </section>
  );
}
